import { useTable } from '@refinedev/core';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TOPUP_LABEL, TOPUP_VARIANT, formatMoney, formatRelative } from '@/lib/format';
import { REGIONS, TOPUP_STATUSES, type TopupStatus } from '@/lib/types';
import { RedeemDialog } from './redeem-dialog';

const ALL = '__all__';

interface TopupRow {
  id: string;
  region: string;
  currency: string;
  face_value: number;
  cost_brl: number;
  provider: string | null;
  status: TopupStatus;
  redeemed_to_account_id: string | null;
  redeemed_at: string | null;
  expires_at: string | null;
  purchased_at: string;
  notes: string | null;
}

export function TopupListPage() {
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [regionFilter, setRegionFilter] = useState<string>(ALL);
  const [redeemTarget, setRedeemTarget] = useState<string | null>(null);

  const { tableQueryResult, current, setCurrent, pageSize, setFilters } = useTable<TopupRow>({
    resource: 'topup',
    pagination: { current: 1, pageSize: 50 },
  });

  const applyFilters = () => {
    const filters = [];
    if (statusFilter !== ALL)
      filters.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    if (regionFilter !== ALL)
      filters.push({ field: 'region', operator: 'eq' as const, value: regionFilter });
    setFilters(filters, 'replace');
    setCurrent(1);
  };

  const data = tableQueryResult.data?.data ?? [];
  const total = tableQueryResult.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gift cards</h1>
          <p className="text-muted-foreground">Inventory de top-ups — {total} no total.</p>
        </div>
        <Button asChild>
          <Link to="/topup/new">
            <Plus className="h-4 w-4" />
            Novo gift card
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos status</SelectItem>
            {TOPUP_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {TOPUP_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Região" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas</SelectItem>
            {REGIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="secondary" onClick={applyFilters}>
          Aplicar
        </Button>
        {(statusFilter !== ALL || regionFilter !== ALL) && (
          <Button
            variant="ghost"
            onClick={() => {
              setStatusFilter(ALL);
              setRegionFilter(ALL);
              setFilters([], 'replace');
              setCurrent(1);
            }}
          >
            Limpar
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Região</TableHead>
              <TableHead className="w-28">Valor</TableHead>
              <TableHead className="w-24">Custo BRL</TableHead>
              <TableHead className="w-28">Provider</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead>Notas</TableHead>
              <TableHead className="w-32">Comprado</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableQueryResult.isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  Nenhum gift card registrado.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Badge variant="outline">{row.region}</Badge>
                  </TableCell>
                  <TableCell className="font-mono">
                    {formatMoney(row.face_value, row.currency)}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {formatMoney(row.cost_brl, 'BRL')}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.provider ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant={TOPUP_VARIANT[row.status]}>{TOPUP_LABEL[row.status]}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                    {row.notes ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatRelative(row.purchased_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.status === 'available' ? (
                      <Button size="sm" variant="outline" onClick={() => setRedeemTarget(row.id)}>
                        Aplicar
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {row.redeemed_at ? formatRelative(row.redeemed_at) : '—'}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Página {current} de {totalPages} ({total} no total)
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={current <= 1}
            onClick={() => setCurrent(current - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={current >= totalPages}
            onClick={() => setCurrent(current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <RedeemDialog
        topupId={redeemTarget}
        open={redeemTarget !== null}
        onOpenChange={(o) => !o && setRedeemTarget(null)}
        onRedeemed={() => tableQueryResult.refetch()}
      />
    </div>
  );
}
